/**
 * The Flavour Machine: six reels (base, a second base, then four mix-ins), each a ring of twelve faces 30 degrees apart
 * (the faces' places are the canvas's CSS). A pull always lands a base and a mix-in, sometimes a second base and more
 * mix-ins. Tap a filled reel to reroll it, × to empty it, an empty one to fill it. Name it and Suggest, one a day.
 * Ingredients come from the specials read (specials/ingredients); Suggest posts specials/suggest.
 */
import { hub, signInUrl } from "./ms-hub.js";

export const FACES = 12;
const STEP = 360 / FACES;
const TURNS = 3;
const REELS = [{ kind: "bases", must: true }, { kind: "bases", must: false }, { kind: "mixIns", must: true }, { kind: "mixIns", must: false }, { kind: "mixIns", must: false }, { kind: "mixIns", must: false }];

const pickFrom = (list, rand, not = []) => {
  const left = list.filter((x) => !not.includes(x));
  return left.length ? left[Math.floor(rand() * left.length) % left.length] : null;
};

/** A pull: [base, base or null, mix-in, then three mix-ins or null], never the same thing twice. */
export function pull(pool, rand = Math.random) {
  const out = [];
  REELS.forEach((r, k) => {
    const chance = k === 1 ? 0.35 : k === 3 ? 0.7 : k === 4 ? 0.4 : 0.15;
    out.push(r.must || rand() < chance ? pickFrom(pool[r.kind] || [], rand, out) : null);
  });
  // A reel left empty in the middle reads oddly: the mix-ins fill from the left.
  const mixes = out.slice(2).filter(Boolean);
  return [out[0], out[1], ...[0, 1, 2, 3].map((i) => mixes[i] ?? null)];
}

/** A reel's twelve faces with the pick on one of them (an empty face when the pick is null), the rest from the pool. */
export function facesFor(pick, list, rand = Math.random, must = false) {
  const others = list.filter((x) => x !== pick);
  const faces = [];
  for (let i = 0; i < FACES; i++) faces.push(others.length ? others[(Math.floor(rand() * others.length) + i) % others.length] : pick);
  const at = Math.floor(rand() * FACES) % FACES;
  faces[at] = pick;
  // An optional reel always has an empty face to land on when it's taken out.
  if (!must && pick !== null) faces[(at + FACES / 2) % FACES] = null;
  return faces;
}

/** Where each ring stops (degrees): three whole turns, then the face holding its pick. */
export function stopsFor(list, picks) {
  return list.map((faces, k) => {
    const i = faces.indexOf(picks[k] ?? null);
    return -(TURNS * 360 + (i < 0 ? 0 : i) * STEP);
  });
}

/** "A and B with C, D and E." */
export function describe(picks) {
  const and = (xs) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`);
  const bases = picks.slice(0, 2).filter(Boolean), mixes = picks.slice(2).filter(Boolean);
  if (!bases.length && !mixes.length) return "";
  return `${and(bases)}${mixes.length ? `${bases.length ? " with " : ""}${and(mixes)}` : ""}.`;
}

/** Suggest needs a name, a base and a mix-in, and isn't today's second. */
export function canSuggest({ name, picks, lastDay, today }) {
  return !!(name || "").trim() && !!(picks[0] || picks[1]) && picks.slice(2).some(Boolean) && lastDay !== today;
}

/** The reels whose pick moved, so a tap on one spins only that one. */
export function changedReels(before, after) {
  return after.map((p, k) => ((p ?? null) !== (before[k] ?? null) ? k : -1)).filter((k) => k >= 0);
}

/** Build your own (Michael, 23 Oct; the app's Dream up): a pick goes on, or comes off if it's there in any case; past the
 * limit (two bases, four mix-ins) it waits. */
/** The machine's picks would replace different ones you'd already made in the form. */
export function replacesPicks(draft, picks) {
  const key = (l) => l.filter(Boolean).map((x) => String(x).toLowerCase()).join("|");
  const had = key(draft.bases) + "/" + key(draft.mixIns);
  if (had === "/") return false;
  return had !== key(picks.slice(0, 2)) + "/" + key(picks.slice(2));
}

export function togglePick(list, item, limit) {
  const key = String(item).toLowerCase();
  if (list.some((x) => x.toLowerCase() === key)) return list.filter((x) => x.toLowerCase() !== key);
  return list.length >= limit ? list : [...list, item];
}

/** What we've made that matches what's typed: starting with it first, then containing it, six at most. */
export function searchPool(pool, typed, limit = 6) {
  const t = String(typed || "").trim().toLowerCase();
  if (!t) return [];
  const starts = pool.filter((x) => x.toLowerCase().startsWith(t));
  const has = pool.filter((x) => !x.toLowerCase().startsWith(t) && x.toLowerCase().includes(t));
  return [...starts, ...has].slice(0, limit);
}

/** Typed exactly as one of ours (any case), it's ours, spelt our way; null when it isn't. */
export function ourSpelling(pool, typed) {
  const t = String(typed || "").trim().toLowerCase();
  return pool.find((x) => x.toLowerCase() === t) ?? null;
}

/** Something new, written as the board writes it: a capital first, the rest as typed. */
export function invented(typed) {
  const t = String(typed || "").trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Build your own can go with a name, a base and a mix-in, once today's isn't sent. */
export function canSend({ name, bases, mixIns, lastDay, today }) {
  return !!String(name || "").trim() && bases.length > 0 && mixIns.length > 0 && lastDay !== today;
}

/** specials/suggest's body: the name, its parts, the pitch only when there is one, and whether it came off the machine. */
export function suggestBody({ name, bases, mixIns, pitch, kind }) {
  const words = String(pitch || "").trim();
  return { name: String(name || "").trim(), bases, mixIns, kind: kind === "random" ? "random" : "build", ...(words ? { pitch: words } : {}) };
}

const SMALL = new Set(["de", "di", "la", "le", "of", "and", "with", "au", "du"]);

/** A chip's words: particles in lower case, and a base without its " Gelato" (the step already says base). */
export function chipWords(name, base = false) {
  const out = String(name).split(" ").map((w, i) => (i && SMALL.has(w.toLowerCase()) ? w.toLowerCase() : w)).join(" ");
  return base && out !== "Gelato" ? out.replace(/ Gelato$/, "") : out;
}

/** The preview's sentence, written as a flavour line is: lower case after the first word, "with" then the mix-ins. */
export function recipeLine(bases, mixIns) {
  const lo = (xs) => xs.map((x) => String(x).toLowerCase());
  const and = (xs) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`);
  const b = lo(bases).join(" and "), m = and(lo(mixIns));
  const line = b + (m ? `${b ? " with " : ""}${m}` : "");
  return line ? `${line.charAt(0).toUpperCase()}${line.slice(1)}.` : "";
}

/** "1 of 2 bases"; once full, how to swap. */
export function countWords(n, of, word) {
  return `<b>${n} of ${of}</b> ${word}${n >= of ? ". Take one off to swap." : ""}`;
}

const DAY_KEY = "ms-suggested-day-v1";
const DRAFT_KEY = "ms-dream-draft-v1";
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });
const readDay = () => { try { return localStorage.getItem(DAY_KEY); } catch { return null; } };
const writeDay = (d) => { try { localStorage.setItem(DAY_KEY, d); } catch { /* the server says no to a second one anyway */ } };
const readDraft = () => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; } };
const writeDraft = (d) => { try { if (d) localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); else localStorage.removeItem(DRAFT_KEY); } catch { /* it just won't keep */ } };
const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const LIMIT = { bases: 2, mixIns: 4 };
const QUICK = { bases: 12, mixIns: 14 };
const PHONE_N = { bases: 6, mixIns: 8 };
const FULL = { bases: "Two bases is the limit. Take one off to swap.", mixIns: "Four mix-ins is the most. Take one off to swap." };

if (typeof window !== "undefined" && typeof customElements !== "undefined") {
  /** The Flavour Machine: pull for a flavour, tap a reel to reroll just that one, × to take one out. Its Suggest it hands the
   * reels' picks to Build your own (ms:machine-suggest), where it's named and sent. */
  class MsMachine extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.picks = [null, null, null, null, null, null];
      this.angles = [0, 0, 0, 0, 0, 0];
      this.plus = this.querySelector("template[data-fm-plus]")?.innerHTML || "";
      this.addEventListener("click", (e) => this.onClick(e));
    }

    /** The ingredients, from Build your own's read; the first time it's shown, a pull. */
    ready(pool) { this.pool = pool; }
    shown() { if (this.pool && !this.picks.some(Boolean)) this.spin(pull(this.pool)); }
    named(name) { const n = this.querySelector("[data-fm-named]"); if (n) { n.textContent = name || "You’ll name it next"; n.classList.toggle("phold", !name); } }
    say(text) { const s = this.querySelector("[data-fm-say]"); if (s) { s.textContent = text; s.hidden = !text; } }

    /** Fills the reels in `only` (every one when it's a pull) around their picks and spins them there. */
    spin(picks, only = null) {
      if (!this.pool) return;
      const which = only ?? picks.map((_, k) => k);
      this.picks = picks;
      this.faces = this.faces || picks.map(() => []);
      which.forEach((k) => { this.faces[k] = facesFor(picks[k], this.pool[REELS[k].kind], Math.random, REELS[k].must); });
      const stops = stopsFor(this.faces, picks);
      const still = matchMedia("(prefers-reduced-motion: reduce)").matches;
      const rings = [...this.querySelectorAll(".ring")];
      which.forEach((k) => {
        const ring = rings[k];
        if (!ring) return;
        ring.querySelectorAll(".face").forEach((f, i) => {
          const v = this.faces[k][i];
          f.classList.toggle("empty", v == null);
          f.innerHTML = v == null ? `${this.plus}<small>Add</small>` : esc(v);
        });
        const from = this.angles[k] % 360;
        this.angles[k] = stops[k];
        ring.style.animation = "none";
        ring.style.transform = `rotateX(${stops[k]}deg)`;
        if (!still) ring.animate([{ transform: `rotateX(${from}deg)` }, { transform: `rotateX(${stops[k] - 10}deg)`, offset: 0.84 }, { transform: `rotateX(${stops[k] + 4}deg)`, offset: 0.93 }, { transform: `rotateX(${stops[k]}deg)` }],
          { duration: 1100 + (only ? 0 : k * 150), easing: "cubic-bezier(.3,.08,.2,1)" });
      });
      this.paint();
    }

    pull() {
      if (!this.pool) return;
      const t = this.querySelector(".fm-toggle");
      if (t) t.checked = !t.checked; // the lever's own pull, drawn by the CSS
      this.spin(pull(this.pool));
    }

    onClick(e) {
      if (e.target.closest("[data-fm-pull]")) { this.pull(); return; }
      const x = e.target.closest(".fm-x");
      if (x) { const k = +x.dataset.k; const p = [...this.picks]; p[k] = null; this.spin(p, [k]); return; }
      const reel = e.target.closest(".fm-reel");
      if (reel && this.pool) { const k = +reel.dataset.k; const p = [...this.picks]; p[k] = pickFrom(this.pool[REELS[k].kind], Math.random, this.picks); this.spin(p, changedReels(this.picks, p)); return; }
      if (e.target.closest("[data-fm-suggest]")) this.dispatchEvent(new CustomEvent("ms:machine-suggest", { bubbles: true, detail: { picks: [...this.picks] } }));
    }

    paint() {
      this.querySelectorAll(".fm-x").forEach((x) => { x.hidden = this.picks[+x.dataset.k] == null; });
      this.querySelectorAll(".fm-reel").forEach((r) => {
        const k = +r.dataset.k, v = this.picks[k], lab = REELS[k].kind === "bases" ? "Base" : "Mix-in";
        r.setAttribute("aria-label", v ? `${lab} ${k < 2 ? k + 1 : k - 1}, ${v}, reroll just this one` : k === 1 ? "Add a second base" : "Add a mix-in");
      });
      const d = this.querySelector("[data-fm-desc]");
      if (d) d.textContent = recipeLine(this.picks.slice(0, 2).filter(Boolean), this.picks.slice(2).filter(Boolean));
    }
  }
  if (!customElements.get("ms-machine")) customElements.define("ms-machine", MsMachine);

  /** Dream up a flavour: Build your own or Randomise on one track, and the one draft both end in. */
  const CHEVRON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"></path></svg>';

  class MsDream extends HTMLElement {
    async connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.signedIn = this.dataset.signedIn === "1";
      this.form = this.querySelector("[data-fm-build]");
      this.done = this.querySelector("[data-fm-done]");
      this.machine = this.querySelector("ms-machine");
      const d = readDraft() || {};
      this.draft = { bases: d.bases || [], mixIns: d.mixIns || [], name: d.name || "", pitch: d.pitch || "", kind: d.kind === "random" ? "random" : "build" };
      this.$("[data-fm-field=name]").value = this.draft.name;
      this.$("[data-fm-field=pitch]").value = this.draft.pitch;
      this.addEventListener("ms:track", (e) => { if (e.target.dataset?.name === "fm-mode") this.mode(e.detail.index === 1 ? "random" : "build"); });
      this.addEventListener("ms:machine-suggest", (e) => this.fromMachine(e.detail.picks));
      this.form.addEventListener("submit", (e) => { e.preventDefault(); this.send(); });
      this.form.addEventListener("click", (e) => { const m = e.target.closest("[data-fm-more]"); if (m) { const box = m.closest(".fm-chips"); m.setAttribute("aria-expanded", "true"); box.classList.add("all"); box.querySelector(".fm-more input")?.focus(); } });
      this.$("[data-fm-signin]")?.addEventListener("click", () => this.save());
      this.$("[data-fm-errs-list]")?.addEventListener("click", (e) => { const a = e.target.closest("a[href^='#']"); if (a) { e.preventDefault(); this.focusMiss(a.getAttribute("href").slice(1)); } });
      this.$("[data-fm-undo]")?.addEventListener("click", () => {
        if (!this.before) return;
        Object.assign(this.draft, this.before); this.before = null;
        this.$("[data-fm-swapped]").hidden = true;
        this.save(); this.chips("bases"); this.chips("mixIns"); this.paint();
        this.$("[data-fm-chips=bases] input")?.focus();
      });
      this.form.addEventListener("change", (e) => { const c = e.target.closest("[data-fm-chip]"); if (c) this.chip(c); });
      this.form.addEventListener("input", (e) => this.typed(e.target));
      this.form.addEventListener("keydown", (e) => this.key(e));
      this.form.addEventListener("focusout", (e) => { const f = e.target.closest("[data-fm-find]"); if (f) setTimeout(() => { if (!f.closest(".fm-find").contains(document.activeElement)) this.close(f); }, 120); });
      this.form.querySelectorAll(".fm-opts").forEach((l) => {
        l.addEventListener("mousedown", (e) => e.preventDefault()); // keeps focus in the search while an option is clicked
        l.addEventListener("click", (e) => { const o = e.target.closest("[role=option]"); if (o) this.choose(l.closest(".fm-find").querySelector("input"), o); });
      });
      this.paintSend();
      this.$(".fm-mode [role=radiogroup]")?.setAttribute("aria-controls", "fm-stage");
      const r = await hub("specials/ingredients", {});
      this.pool = r.ok && r.data?.bases?.length && r.data?.mixIns?.length ? { bases: r.data.bases, mixIns: r.data.mixIns } : { bases: [], mixIns: [] };
      if (!this.pool.bases.length) this.fail("We can’t load what we’ve made right now, so everything you type counts as new.");
      else this.machine?.ready(this.pool);
      this.chips("bases"); this.chips("mixIns"); this.paint();
    }

    $(sel) { return this.querySelector(sel); }
    save() { writeDraft(this.draft); if (this.kept) { this.kept = false; this.paintSend(); } }
    fail(text) { const f = this.$("[data-fm-fail]"); if (f) { f.textContent = text; f.hidden = !text; } }

    /** Build your own shows the form (or what was just sent); Randomise shows the machine. Never both. */
    mode(m) {
      const random = m === "random";
      this.machine.hidden = !random;
      this.form.hidden = random;
      this.done.hidden = true;
      if (random) { this.machine.named(this.draft.name.trim()); this.machine.shown(); }
    }

    /** The reels' picks into the form, at Name it, as one off the machine. */
    /** Your own change after the machine's swap: there's nothing left to undo. */
    settle() {
      this.before = null; const n = this.$("[data-fm-swapped]"); if (n) n.hidden = true;
      this.$("#fm-name-in")?.setAttribute("aria-describedby", "fm-name-err fm-name-n");
    }

    fromMachine(picks) {
      // picks you'd made in the form are kept for one Undo, and the swap is said
      const swapped = replacesPicks(this.draft, picks);
      this.before = swapped ? { bases: [...this.draft.bases], mixIns: [...this.draft.mixIns], kind: this.draft.kind } : null;
      const note = this.$("[data-fm-swapped]");
      if (note) note.hidden = !swapped;
      const nameIn = this.$("#fm-name-in");
      nameIn?.setAttribute("aria-describedby", `${swapped ? "fm-swapped " : ""}fm-name-err fm-name-n`);
      this.draft.bases = picks.slice(0, 2).filter(Boolean);
      this.draft.mixIns = picks.slice(2).filter(Boolean);
      this.draft.kind = "random";
      this.save(); this.chips("bases"); this.chips("mixIns"); this.paint();
      this.querySelector('.fm-mode [role="radio"]')?.click();
      const name = this.$("[data-fm-field=name]");
      name.closest("fieldset").scrollIntoView({ block: "start", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
      name.focus({ preventScroll: true });
    }

    /** One step's chips: what you picked that isn't among the ones we use most first, then those. */
    chips(kind) {
      const box = this.$(`[data-fm-chips=${kind}]`);
      if (!box || !this.pool) return;
      const quick = this.pool[kind].slice(0, QUICK[kind]);
      const own = this.draft[kind].filter((x) => !quick.some((q) => q.toLowerCase() === x.toLowerCase()));
      const on = (x) => this.draft[kind].some((p) => p.toLowerCase() === x.toLowerCase());
      const name = kind === "bases" ? "base" : "mix-in";
      const chip = (x, i, mine) => `<label class="chip check-chip${mine ? " fm-own" : ""}${!mine && i >= PHONE_N[kind] && !on(x) ? " fm-more" : ""}${on(x) ? " on" : ""}"><input type="checkbox" name="${name}" value="${esc(x)}" data-fm-chip="${kind}"${on(x) ? " checked" : ""}><span class="tick">${this.tick()}</span>${esc(chipWords(x, kind === "bases"))}</label>`;
      const hidden = quick.filter((x, i) => i >= PHONE_N[kind] && !on(x)).length;
      const more = hidden ? `<button class="chip fm-more-btn" type="button" aria-expanded="false" aria-controls="${box.id}" data-fm-more>Show all ${own.length + quick.length}${CHEVRON}</button>` : "";
      box.innerHTML = own.map((x) => chip(x, 0, true)).join("") + quick.map((x, i) => chip(x, i, false)).join("") + more;
    }

    tick() { return this.tickSvg ??= this.$(".check-chip .tick")?.innerHTML || '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg>'; }

    chip(input) {
      const kind = input.dataset.fmChip, before = this.draft[kind];
      const after = togglePick(before, input.value, LIMIT[kind]);
      if (after === before) { input.checked = false; this.count(kind, FULL[kind]); return; }
      this.draft[kind] = after; this.settle();
      input.closest(".check-chip").classList.toggle("on", input.checked);
      if (!input.checked && input.closest(".fm-own")) { this.chips(kind); this.$(`[data-fm-find=${kind}]`)?.focus(); }
      this.save(); this.paint();
    }

    count(kind, override = "") {
      const c = this.$(`[data-fm-count=${kind}]`);
      if (c) c.innerHTML = override ? `<b>${this.draft[kind].length} of ${LIMIT[kind]}</b> ${esc(override)}` : countWords(this.draft[kind].length, LIMIT[kind], kind === "bases" ? "bases" : "mix-ins");
      const box = this.$(`[data-fm-chips=${kind}]`), full = this.draft[kind].length >= LIMIT[kind];
      box?.classList.toggle("at-limit", full);
      box?.querySelectorAll("input[data-fm-chip]").forEach((i) => {
        if (full && !i.checked) { i.setAttribute("aria-disabled", "true"); if (c?.id) i.setAttribute("aria-describedby", c.id); }
        else { i.removeAttribute("aria-disabled"); i.removeAttribute("aria-describedby"); }
      });
    }

    typed(el) {
      if (el.matches("[data-fm-find]")) { this.list(el); return; }
      const f = el.dataset.fmField;
      if (!f) return;
      this.draft[f] = el.value;
      if (f === "name" && el.value.trim()) { el.removeAttribute("aria-invalid"); this.$("#fm-name-err").hidden = true; }
      this.save(); this.paint();
    }

    /** The search's list: what we've made that matches, then Invent unless it's exactly one of ours. */
    list(input, at = 0) {
      const kind = input.dataset.fmFind, ul = document.getElementById(input.getAttribute("aria-controls"));
      const t = input.value.trim();
      // what isn't already a chip comes first
      const shown = new Set(this.pool ? this.pool[kind].slice(0, QUICK[kind]).map((x) => x.toLowerCase()) : []);
      const found = this.pool ? searchPool(this.pool[kind], t) : [];
      const hits = [...found.filter((h) => !shown.has(h.toLowerCase())), ...found.filter((h) => shown.has(h.toLowerCase()))];
      const ours = this.pool ? ourSpelling(this.pool[kind], t) : null;
      const picked = (h) => this.draft[kind].some((x) => x.toLowerCase() === h.toLowerCase());
      const full = this.draft[kind].length >= LIMIT[kind];
      const opts = hits.map((h, i) => `<li role="option" id="${ul.id}-o${i}" class="fm-opt${picked(h) ? " picked" : ""}${full && !picked(h) ? " off" : ""}" aria-selected="${picked(h)}"${full && !picked(h) ? ' aria-disabled="true"' : ""} data-value="${esc(h)}">${esc(chipWords(h))}${picked(h) ? `<span class="tick">${this.tick()}</span>` : ""}</li>`);
      if (t && !ours && !full) opts.push(`<li role="option" id="${ul.id}-new" class="fm-opt fm-invent" aria-selected="false" data-value="${esc(invented(t))}" data-new="1">${this.plus()}<span>Invent “${esc(t)}”</span></li>`);
      ul.innerHTML = opts.join("");
      const open = opts.length > 0;
      ul.hidden = !open;
      input.setAttribute("aria-expanded", String(open));
      const n = this.$(`[data-fm-find-n=${kind}]`);
      if (n) n.textContent = !t ? "" : full ? `${hits.length} we’ve made. ${FULL[kind]}` : `${hits.length ? `${hits.length} we’ve made` : "None we’ve made"}${!ours ? ", or invent it" : ""}. Arrow down to pick.`;
      this.active(input, open ? Math.min(at, opts.length - 1) : -1);
    }

    plus() { return this.machine?.plus || ""; }

    active(input, i) {
      const ul = document.getElementById(input.getAttribute("aria-controls"));
      const opts = [...ul.querySelectorAll("[role=option]")];
      opts.forEach((o, k) => o.classList.toggle("active", k === i));
      if (opts[i]) { input.setAttribute("aria-activedescendant", opts[i].id); opts[i].scrollIntoView({ block: "nearest" }); }
      else input.removeAttribute("aria-activedescendant");
      input.dataset.active = String(i);
    }

    close(input) {
      const ul = document.getElementById(input.getAttribute("aria-controls"));
      if (ul) { ul.hidden = true; ul.innerHTML = ""; }
      input.setAttribute("aria-expanded", "false");
      input.removeAttribute("aria-activedescendant");
    }

    key(e) {
      const input = e.target.closest("[data-fm-find]");
      if (!input) return;
      const ul = document.getElementById(input.getAttribute("aria-controls"));
      const n = ul.querySelectorAll("[role=option]").length, i = +(input.dataset.active ?? -1);
      if (e.key === "ArrowDown" && n) { e.preventDefault(); this.active(input, (i + 1) % n); }
      else if (e.key === "ArrowUp" && n) { e.preventDefault(); this.active(input, (i - 1 + n) % n); }
      else if (e.key === "Enter") { e.preventDefault(); const o = ul.querySelectorAll("[role=option]")[i]; if (o) this.choose(input, o); }
      else if (e.key === "Escape" && !ul.hidden) { e.preventDefault(); this.close(input); }
    }

    /** A search pick: on, as its own chip if it isn't one of the quick ones, unless the step is full. */
    choose(input, option) {
      const kind = input.dataset.fmFind, value = option.dataset.value;
      if (this.draft[kind].some((x) => x.toLowerCase() === value.toLowerCase())) {
        // picked already: choosing it again takes it off, as its chip would, and the list stays where you were
        this.draft[kind] = togglePick(this.draft[kind], value, LIMIT[kind]);
        this.chips(kind); this.save(); this.paint(); this.list(input, +(input.dataset.active ?? 0));
        return;
      }
      if (this.draft[kind].length >= LIMIT[kind]) { this.count(kind, FULL[kind]); return; }
      this.draft[kind] = [...this.draft[kind], value]; this.settle();
      input.value = ""; this.close(input);
      const said = this.$(`[data-fm-find-n=${kind}]`); if (said) said.textContent = `Added ${chipWords(value, kind === "bases")}.`;
      this.chips(kind); this.save(); this.paint();
      input.focus();
    }

    paint() {
      this.count("bases"); this.count("mixIns");
      if (this.draft.bases.length) { const be = this.$("#fm-base-err"); if (be) be.hidden = true; }
      // the recipe reads once there's a base; until then the sticker says what to do
      const name = this.draft.name.trim(), line = this.draft.bases.length ? recipeLine(this.draft.bases, this.draft.mixIns) : "";
      const pn = this.$("[data-fm-pv-name]"), pr = this.$("[data-fm-pv-recipe]");
      if (pn) { pn.textContent = name || "Your flavour"; pn.classList.toggle("phold", !name); }
      if (pr) { pr.dataset.empty ??= pr.textContent; pr.textContent = line || pr.dataset.empty; }
      this.querySelectorAll("[data-fm-n]").forEach((n) => { const f = n.dataset.fmN; n.textContent = `${this.draft[f].length} of ${f === "name" ? 42 : 240}`; });
      this.machine?.named(name);
    }

    paintSend() {
      const later = readDay() === today();
      this.$("[data-fm-later]").hidden = !later;
      const ln = this.$("[data-fm-later-note]");
      ln.hidden = !later;
      if (ln.dataset.before) ln.textContent = ln.dataset.before;
      const signin = this.$(".fm-signin:not(.fm-later)");
      if (signin) signin.hidden = later;
      this.$("[data-fm-send-note]").hidden = later;
      this.$("[data-fm-send-label]").textContent = later ? "Keep it for tomorrow" : this.signedIn ? "Suggest it" : "Sign in to suggest it";
      // the button's icon says what it does: send, sign in, or keep it for tomorrow
      const ic = later ? "later" : this.signedIn ? "send" : "signin";
      this.querySelectorAll("[data-fm-ic]").forEach((el) => { el.hidden = el.dataset.fmIc !== ic; });
    }

    /** What's missing, said at the top with links, and on the name itself. */
    errors() {
      const miss = [];
      if (!this.draft.bases.length) miss.push(["fm-bases", "A base"]);
      if (!this.draft.mixIns.length) miss.push(["fm-mixes", "A mix-in"]);
      if (!this.draft.name.trim()) miss.push(["fm-name-in", "Its name"]);
      const box = this.$("[data-fm-errs]");
      const nameIn = this.$("#fm-name-in");
      const noName = miss.some(([id]) => id === "fm-name-in");
      this.$("#fm-name-err").hidden = !noName;
      const be = this.$("#fm-base-err"); if (be) be.hidden = this.draft.bases.length > 0;
      if (noName) nameIn.setAttribute("aria-invalid", "true"); else nameIn.removeAttribute("aria-invalid");
      box.hidden = miss.length < 2;
      if (miss.length === 1) this.focusMiss(miss[0][0]);
      if (miss.length > 1) {
        this.$("[data-fm-errs-title]").textContent = miss.length === 1 ? "One thing before it can go" : `${["", "", "Two", "Three"][miss.length]} things before it can go`;
        this.$("[data-fm-errs-list]").innerHTML = miss.map(([id, t]) => `<li><a href="#${id}">${t}</a></li>`).join("");
        box.focus();
      }
      return miss.length === 0;
    }

    /** A missing step's place: its first chip for bases and mix-ins, else the field. */
    focusMiss(id) { (id === "fm-bases" || id === "fm-mixes" ? this.$(`#${id} input`) : document.getElementById(id))?.focus(); }

    async send() {
      this.fail("");
      if (!this.errors()) return;
      this.save();
      if (readDay() === today()) {
        const ln = this.$("[data-fm-later-note]"); ln.dataset.before ??= ln.textContent; ln.textContent = ln.dataset.kept || ln.textContent;
        this.kept = true; this.$("[data-fm-send-label]").textContent = "Kept for tomorrow";
        this.querySelectorAll("[data-fm-ic]").forEach((el) => { el.hidden = el.dataset.fmIc !== "kept"; });
        return;
      }
      if (!this.signedIn) { location.href = signInUrl(`${location.pathname}#dream`); return; }
      const btn = this.$("[data-fm-send]");
      btn.setAttribute("aria-disabled", "true");
      const r = await hub("specials/suggest", suggestBody(this.draft));
      btn.removeAttribute("aria-disabled");
      if (r.ok) { writeDay(today()); this.sent(); return; }
      if (r.data?.reason === "daily") { writeDay(today()); this.paintSend(); return; }
      if (r.data?.reason === "moderated" && r.data?.message) { this.fail(r.data.message); return; }
      this.fail("We couldn’t send that just now. Try again in a moment.");
    }

    sent() {
      this.$("[data-fm-done-name]").textContent = this.draft.name.trim();
      this.$("[data-fm-done-recipe]").textContent = recipeLine(this.draft.bases, this.draft.mixIns);
      this.draft = { bases: [], mixIns: [], name: "", pitch: "", kind: "build" };
      writeDraft(null);
      this.form.reset(); this.chips("bases"); this.chips("mixIns"); this.paint(); this.paintSend();
      this.form.hidden = true; this.done.hidden = false;
      this.done.querySelector("h3")?.focus();
    }
  }
  if (!customElements.get("ms-dream")) customElements.define("ms-dream", MsDream);
}
